import { useMemo, useState, useCallback } from "react";
import { toParams } from "@/utils/toParams";

function shallowEqualOptions(a = [], b = []) {
	if (a === b) return true;
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) {
		const va = a[i],
			vb = b[i];
		const ka = va?.value ?? va?.id ?? va;
		const kb = vb?.value ?? vb?.id ?? vb;
		if (ka !== kb) return false;
	}
	return true;
}

export default function useListController({ baseFilter, baseTable }) {
	const [isFilterOpen, setIsFilterOpen] = useState(false);
	const [filtersState, setFiltersState] = useState([...baseFilter]);
	const [appliedFiltersState, setAppliedFiltersState] = useState([...baseFilter]);
	const [tableState, setTableState] = useState({ ...baseTable });
	const [appliedFilterParams, setAppliedFilterParams] = useState(() => toParams(baseFilter));

	const setFilterOptions = useCallback((key, options, autoSelect = true) => {
		const opts = options ?? [];
		let nextState = null;
		let didChange = false;

		setFiltersState((prev) => {
			let changedLocal = false;

			const next = prev.map((f) => {
				if (f.key !== key) return f;

				const only = opts.length === 1 ? opts[0] : null;
				const onlyValue = only?.value ?? only?.id ?? only;

				const isEmptyMulti = f.multiple && (!Array.isArray(f.value) || f.value.length === 0);
				const isEmptySingle =
					!f.multiple && (f.value === undefined || f.value === null || f.value === "" || (Array.isArray(f.value) && f.value.length === 0));

				let newValue = f.value;
				let valueChanged = false;

				if (autoSelect && only && (isEmptyMulti || isEmptySingle)) {
					newValue = f.multiple ? [onlyValue] : onlyValue;
					valueChanged = JSON.stringify(newValue) !== JSON.stringify(f.value);
				}

				const optionsChanged = !shallowEqualOptions(f.options || [], opts);

				if (optionsChanged || valueChanged) {
					changedLocal = true;
					return {
						...f,
						options: opts,
						value: valueChanged ? newValue : f.value,
					};
				}
				return f;
			});

			if (!changedLocal) {
				nextState = prev;
				didChange = false;
				return prev;
			}

			nextState = next;
			didChange = true;
			return next;
		});

		if (didChange && nextState) {
			setAppliedFilterParams(toParams(nextState));
			setAppliedFiltersState([...nextState]);
			setTableState((s) => ({ ...s, page: 1 }));
		}
	}, []);

	const reactTableSort = useMemo(() => [{ id: tableState.order_by, desc: tableState.order_dir === "desc" }], [tableState.order_by, tableState.order_dir]);

	const openFilter = () => setIsFilterOpen(true);
	const closeFilter = () => setIsFilterOpen(false);

	const handleFilterChange = (filterKey, filterValue) => setFiltersState((prev) => prev.map((f) => (f.key === filterKey ? { ...f, value: filterValue } : f)));

	const handleClearAllFilters = () => {
		const cleared = filtersState.map((f) => {
			if (f.key === "specie") return f;
			if (f.type === "switch") return { ...f, value: false };
			if (f.type === "select" && f.multiple) return { ...f, value: "" };
			return { ...f, value: "" };
		});
		setFiltersState(cleared);
		setAppliedFiltersState([...cleared]);
		setAppliedFilterParams(toParams(cleared));
		setIsFilterOpen(false);
		setTableState((state) => ({ ...state, page: 1 }));
	};

	const handleClearOneFilter = (key) => {
		const cleared = filtersState.map((f) => {
			if (f.key !== key) return f;
			if (f.type === "switch") return { ...f, value: false };
			if (f.type === "select" && f.multiple) return { ...f, value: "" };
			return { ...f, value: "" };
		});
		setFiltersState(cleared);
		setAppliedFiltersState([...cleared]);
		setAppliedFilterParams(toParams(cleared));
		setTableState((state) => ({ ...state, page: 1 }));
	};

	const handleApplyFilters = () => {
		setAppliedFilterParams(toParams(filtersState));
		setAppliedFiltersState([...filtersState]);
		setIsFilterOpen(false);
		setTableState((s) => ({ ...s, page: 1 }));
	};

	const applyFilterOverrides = useCallback((overrides) => {
		setFiltersState((prev) => {
			const next = prev.map((filterItem) => {
				const overrideValue = overrides[filterItem.key];
				return overrideValue === undefined ? filterItem : { ...filterItem, value: overrideValue };
			});
			setAppliedFilterParams(toParams(next));
			setAppliedFiltersState([...next]);
			return next;
		});
		setTableState((s) => ({ ...s, page: 1 }));
	}, []);

	const handlePageChange = (newPage1) => {
		const next = Math.max(1, Number(newPage1 || 1));
		setTableState((s) => (s.page === next ? s : { ...s, page: next }));
	};

	const handlePageSizeChange = (newSize) => {
		const nextSize = Math.max(1, Number(newSize || 20));
		setTableState((s) => (s.per_page === nextSize ? s : { ...s, per_page: nextSize, page: 1 }));
	};

	const handleSortByChange = (rtSortArray) => {
		const primary = rtSortArray?.[0];
		if (!primary) return;
		const nextOrderBy = primary.id;
		const nextOrderDir = primary.desc ? "desc" : "asc";
		setTableState((s) =>
			s.order_by === nextOrderBy && s.order_dir === nextOrderDir ? s : { ...s, order_by: nextOrderBy, order_dir: nextOrderDir, page: 1 },
		);
	};

	return {
		isFilterOpen,
		filtersState,
		appliedFiltersState,
		tableState,
		appliedFilterParams,
		reactTableSort,
		openFilter,
		closeFilter,
		handleFilterChange,
		handleApplyFilters,
		applyFilterOverrides,
		handleClearAllFilters,
		handleClearOneFilter,
		handlePageChange,
		handlePageSizeChange,
		handleSortByChange,
		setFilterOptions,
	};
}
